import React, { useMemo, useState } from "react";
import {
  USAGE_CATEGORY_LABELS,
  UsageCategory,
  UsageGroupMetaDTO,
} from "../../../api/admin/types";
import { visibleCategories } from "../shared/constants";
import { formatInt } from "../shared/formatters";

export interface UserTableRow {
  user: UsageGroupMetaDTO;
  totals: Record<UsageCategory, number>;
  total: number;
}

interface UserTableProps {
  rows: UserTableRow[];
  filterTeamId?: string | null;
  onUserClick?: (userId: string) => void;
  caption?: string;
  showEpic?: boolean;
}

type SortKey = "user" | UsageCategory | "total";

const UserTable: React.FC<UserTableProps> = ({
  rows,
  filterTeamId,
  onUserClick,
  caption,
  showEpic = true,
}) => {
  const cats = visibleCategories(showEpic);
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortAsc, setSortAsc] = useState(false);

  const sortedRows = useMemo(() => {
    const r = rows.filter((row) => !filterTeamId || row.user.team_id === filterTeamId);

    return [...r].sort((a, b) => {
      // Push dormant users (zero across the board) to the bottom regardless
      // of sort direction so the "real" rows stay grouped together.
      const aDormant = a.total === 0;
      const bDormant = b.total === 0;
      if (aDormant !== bDormant) return aDormant ? 1 : -1;
      let av = 0;
      let bv = 0;
      if (sortKey === "user") {
        return sortAsc
          ? a.user.label.localeCompare(b.user.label)
          : b.user.label.localeCompare(a.user.label);
      }
      if (sortKey === "total") {
        av = a.total;
        bv = b.total;
      } else {
        av = a.totals[sortKey] ?? 0;
        bv = b.totals[sortKey] ?? 0;
      }
      return sortAsc ? av - bv : bv - av;
    });
  }, [rows, filterTeamId, sortKey, sortAsc]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortAsc((s) => !s);
    else {
      setSortKey(k);
      setSortAsc(false);
    }
  };

  const th = (k: SortKey, _label: string): React.CSSProperties => ({
    cursor: "pointer",
    padding: "8px 6px",
    textAlign: k === "user" ? "left" : "right",
    fontSize: 12,
    color: sortKey === k ? "#3949ab" : "#6b7280",
    fontWeight: 700,
    borderBottom: "1px solid #e5e7eb",
    // Allow long category headers to wrap rather than forcing the table wider
    // than the container (which produced a faint horizontal scrollbar).
    whiteSpace: k === "user" ? "nowrap" : "normal",
  });

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      {caption ? (
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>{caption}</div>
      ) : null}
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
        <thead>
          <tr>
            <th style={th("user", "User")} onClick={() => onSort("user")}>
              User {sortKey === "user" ? (sortAsc ? "▲" : "▼") : ""}
            </th>
            {cats.map((c) => {
              // `policies` (uploads) is shown for completeness but doesn't
              // contribute to Total - dim it so admins can tell at a glance.
              const isCompanion = c === "policies";
              return (
                <th
                  key={c}
                  style={{
                    ...th(c, USAGE_CATEGORY_LABELS[c]),
                    color: isCompanion ? "#9ca3af" : th(c, "").color,
                    fontStyle: isCompanion ? "italic" : "normal",
                  }}
                  onClick={() => onSort(c)}
                  title={isCompanion ? "Uploads only - not counted in Total" : undefined}
                >
                  {USAGE_CATEGORY_LABELS[c]}
                  {sortKey === c ? (sortAsc ? " ▲" : " ▼") : ""}
                </th>
              );
            })}
            <th style={th("total", "Total")} onClick={() => onSort("total")}>
              Total {sortKey === "total" ? (sortAsc ? "▲" : "▼") : ""}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 ? (
            <tr>
              <td colSpan={cats.length + 2} style={{ padding: 16, color: "#6b7280" }}>
                No users in this team for the current filter.
              </td>
            </tr>
          ) : (
            sortedRows.map(({ user, totals, total }) => {
              const dormant = total === 0;
              return (
                <tr
                  key={user.id}
                  onClick={() => onUserClick?.(user.id)}
                  style={{
                    cursor: onUserClick ? "pointer" : "default",
                    background: "#fff",
                    opacity: dormant ? 0.55 : 1,
                  }}
                >
                  <td style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6" }}>
                    <div style={{ fontWeight: 600 }}>{user.label}</div>
                    {user.email ? (
                      <div style={{ fontSize: 11, color: "#6b7280" }}>{user.email}</div>
                    ) : null}
                    {user.team_name ? (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{user.team_name}</div>
                    ) : null}
                    {user.role ? (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{user.role}</div>
                    ) : null}
                  </td>
                  {cats.map((c) => {
                    const isCompanion = c === "policies";
                    return (
                      <td
                        key={c}
                        style={{
                          padding: "8px 6px",
                          borderBottom: "1px solid #f3f4f6",
                          textAlign: "right",
                          color: isCompanion ? "#9ca3af" : undefined,
                          fontStyle: isCompanion ? "italic" : "normal",
                          background: isCompanion ? "#fafafa" : undefined,
                        }}
                      >
                        {formatInt(totals[c])}
                      </td>
                    );
                  })}
                  <td
                    style={{
                      padding: "8px 6px",
                      borderBottom: "1px solid #f3f4f6",
                      textAlign: "right",
                      fontWeight: 700,
                    }}
                  >
                    {formatInt(total)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;
